import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react'; // For collapse animation

import ExampleCarouselImage1 from '../arktemis media/pexels-spacex-586063.jpg';
import ExampleCarouselImage2 from '../arktemis media/pexels-kelly-1179532-2876514.jpg';
import ExampleCarouselImage3 from '../arktemis media/pexels-czapp-arpad-3647289-11802280.jpg';
import ExampleCarouselImage4 from '../arktemis media/pexels-cottonbro-5989933.jpg';
import ExampleCarouselImage5 from '../arktemis media/pexels-artempodrez-5716001.jpg';
import ExampleCarouselImage6 from '../arktemis media/pexels-pixabay-459728.jpg';
import ExampleCarouselImage7 from '../arktemis media/service3.jpg';

const images = [
  {
    src: ExampleCarouselImage1,
    title: 'Geospatial Intelligence & Spatial Decision Making',
    description: [
      'Satellite Data & Remote Sensing – High-resolution satellite imagery for environmental monitoring, disaster management, and land-use planning.',
      'Geospatial Data Products – Custom maps, 3D terrain models, and GIS-based analytics.',
      'Geospatial Intelligence & Spatial Decision-Making – Using GIS for land planning, resource management, and predictive modeling.',
      'Urban & Regional Planning – Supporting cities with data-backed infrastructure planning and policy recommendations.'
    ],
  },
  {
    src: ExampleCarouselImage2,
    title: 'Hydrological & Water Resource Management',
    description: [
      'Hydrological & Watershed Analysis – Understanding water flow, runoff, and conservation methods.',
      'Surface Water Management – Water retention, river system mapping, command area mapping and flood prevention.',
      'Groundwater Aquifer System – Mapping groundwater reserves and recharge areas.',
      'Water Balance Analysis – Studying water consumption, availability, and conservation methods.',
      'Watershed Treatment Plan – Using geospatial tools to design erosion control and forest conservation strategies.'
    ],
  },
  {
    src: ExampleCarouselImage3,
    title: 'Sustainable Agriculture & Precision Farming',
    description: [
      'Decision Support for Crop Management – Decision-driven insights for crop health, irrigation, and soil conditions.',
      'Precision Farming – Using IoT sensors, drones, and automation to optimize resource use.',
      'Soil Health & Regenerative Farming – Organic and sustainable farming strategies.'
    ],
  },
  {
    src: ExampleCarouselImage7,
    title: 'Robotics and Automation',
    description: [
      'Sensor suite and IoT – Decision-driven insights for crop health, irrigation, and soil conditions.',
      'Precision Farming – Using IoT sensors, drones, and automation to optimize resource use.'
    ],
  },
  {
    src: ExampleCarouselImage5,
    title: 'Resource Modeling & Decision Support Systems',
    description: [
      'Hydrological Modeling – GIS-based simulations for river flow and flood management.',
      'Ecosystem Restoration Planning – Reforestation and biodiversity conservation modeling.',
      'Climate Risk & Vulnerability Assessments – Identifying risks to infrastructure, agriculture, and communities.',
      'Integrated Resource Management – Balancing land, water, and energy usage for sustainability.'
    ],
  },
  {
    src: ExampleCarouselImage4,
    title: 'ESG Compliance & Policy Advisory',
    description: [
      'Sustainability & ESG Reporting – Helping organizations align with global sustainability goals.',
      'Regulatory Compliance – Ensuring businesses meet environmental laws and standards.',
      'Policy Development & Impact Analysis – Advising governments and corporations on sustainable strategies.',
      'Carbon Credits & Offsetting – Assessing carbon footprints and sustainable land use.'
    ],
  },
  {
    src: ExampleCarouselImage6,
    title: 'Industrial & Infrastructure Analytics',
    description: [
      'Supply Chain & Logistics Optimization – Using data analytics for efficient material flow and transportation.',
      'Industrial Water & Energy Management – Sustainable usage monitoring for factories and production units.',
      'Infrastructure Resilience Planning – Climate impact assessments for roads, bridges, and critical infrastructure.',
      'Smart Cities & IoT Integration – Leveraging data for urban planning and resource efficiency.'
    ],
  },
];

const BootstrapCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);

  const toggleCollapse = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index) => {
    setActiveIndex(index);
    setOpenIndex(null); // Close collapse when changing slide
  };

  const nextSlide = () => {
    setActiveIndex((current) => (current + 1) % images.length);
    setOpenIndex(null);
  };

  const prevSlide = () => {
    setActiveIndex((current) => (current - 1 + images.length) % images.length);
    setOpenIndex(null);
  };

  return (
    <div className="relative w-full min-h-screen bg-black flex items-center justify-center">
      {images.map((item, index) => (
        <div
          key={index}
          className={`absolute top-0 left-0 w-full h-full transition-opacity duration-1000 ease-in-out
                      ${index === activeIndex ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={item.src}
            alt={`Slide ${index + 1}`}
            className="w-full h-full object-cover brightness-50"
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-black bg-opacity-75 p-6 rounded-lg max-w-3xl text-white text-center" data-aos="fade-in">
              <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">{item.title}</h3>

              {/* Large screen full description */}
              <ul className="hidden md:block list-disc list-inside text-left text-lg space-y-2">
                {item.description.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>

              {/* Small screen collapsible */}
              <div className="block md:hidden mt-4">
                <button
                  className="relative inline-flex items-center justify-center p-0.5 overflow-hidden text-sm font-medium text-gray-900 rounded-lg group bg-gradient-to-br from-green-400 to-blue-600 group-hover:from-green-400 group-hover:to-blue-600 hover:text-white dark:text-white focus:ring-4 focus:outline-none focus:ring-green-200 dark:focus:ring-green-800"
                  onClick={() => toggleCollapse(index)}
                >
                  <span className="relative px-5 py-2.5 transition-all ease-in duration-75 bg-white dark:bg-gray-900 rounded-md group-hover:bg-opacity-0">
                    {openIndex === index ? 'Hide Details' : 'Read More'}
                  </span>
                </button>

                <Transition
                  show={openIndex === index}
                  enter="transition-all ease-in-out duration-500"
                  enterFrom="opacity-0 max-h-0"
                  enterTo="opacity-100 max-h-screen"
                  leave="transition-all ease-in-out duration-300"
                  leaveFrom="opacity-100 max-h-screen"
                  leaveTo="opacity-0 max-h-0"
                >
                  <div className="mt-3 text-left bg-gray-700 p-4 rounded-lg text-base overflow-hidden">
                    <ul className="list-disc list-inside space-y-1">
                      {item.description.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Carousel Controls */}
      <button
        className="absolute top-1/2 left-4 -translate-y-1/2 bg-black bg-opacity-50 p-3 rounded-full text-white text-2xl
                   hover:bg-opacity-75 transition-colors duration-300 z-20"
        onClick={prevSlide}
      >
        &larr;
      </button>
      <button
        className="absolute top-1/2 right-4 -translate-y-1/2 bg-black bg-opacity-50 p-3 rounded-full text-white text-2xl
                   hover:bg-opacity-75 transition-colors duration-300 z-20"
        onClick={nextSlide}
      >
        &rarr;
      </button>

      {/* Carousel Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
        {images.map((_, index) => (
          <button
            key={index}
            className={`w-3 h-3 rounded-full bg-white ${index === activeIndex ? 'opacity-100' : 'opacity-50'}
                       hover:opacity-75 transition-opacity duration-300`}
            onClick={() => goToSlide(index)}
          ></button>
        ))}
      </div>
    </div>
  );
};

export default BootstrapCarousel;